import { useEffect, useState } from "react";
import { X, Anchor, Gauge, Compass, Navigation2, Ruler, MapPin, Clock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { CATEGORY_HEX, categoryLabel, navStatusText, timeAgo } from "@/lib/maritime";
import api from "@/lib/api";

const WINDOWS = [
  { key: "6h", label: "6h", hours: 6 },
  { key: "24h", label: "24h", hours: 24 },
  { key: "7d", label: "7d", hours: 168 },
  { key: "30d", label: "30d", hours: 720 },
];

const Stat = ({ icon: Icon, label, value, unit }) => (
  <div className="rounded-lg bg-slate-800/50 border border-slate-700/50 p-2.5">
    <div className="flex items-center gap-1.5 text-slate-500">
      <Icon className="h-3 w-3" />
      <span className="text-[9px] font-mono uppercase tracking-widest">{label}</span>
    </div>
    <div className="mt-1 font-mono text-lg font-bold text-white tabular-nums">
      {value ?? "—"}
      {unit && value != null && (
        <span className="text-xs text-slate-500 ml-1 font-medium">{unit}</span>
      )}
    </div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-slate-800/50 last:border-0">
    <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">
      {label}
    </span>
    <span className="text-sm text-slate-200 font-medium">{value ?? "—"}</span>
  </div>
);

export const VesselPanel = ({ mmsi, onClose, showTrack, setShowTrack, trackWindow, setTrackWindow }) => {
  const [v, setV] = useState(null);

  useEffect(() => {
    let live = true;
    setV(null);
    api.vessel(mmsi).then((d) => live && setV(d)).catch(() => {});
    return () => {
      live = false;
    };
  }, [mmsi]);

  const hex = v ? CATEGORY_HEX[v.category] : "#9CA3AF";

  return (
    <aside
      data-testid="vessel-detail-panel"
      className="absolute top-16 right-4 bottom-16 z-20 w-80 md:w-96 flex flex-col rounded-xl bg-slate-900/95 backdrop-blur-xl border border-slate-800/80 shadow-2xl overflow-hidden st-fade"
    >
      <div className="flex items-start justify-between p-4 border-b border-slate-800/80">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="h-3 w-3 rounded-full shrink-0"
              style={{ backgroundColor: hex }}
            />
            <span
              className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded"
              style={{ color: hex, background: `${hex}22` }}
            >
              {v ? categoryLabel(v.category) : "…"}
            </span>
          </div>
          <h2 className="font-display text-lg font-bold text-white truncate">
            {v ? (v.name || "Unknown vessel") : "Loading…"}
          </h2>
          <p className="font-mono text-[11px] text-slate-500 mt-0.5">
            {v?.flag?.country} ({v?.flag?.iso2}) · {v?.call_sign || "—"}
          </p>
        </div>
        <button
          data-testid="vessel-panel-close"
          onClick={onClose}
          className="text-slate-500 hover:text-slate-200 transition-colors shrink-0"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto st-scroll p-4 space-y-4">
        <div className="grid grid-cols-2 gap-2">
          <Row label="MMSI" value={v?.mmsi} />
          <Row label="IMO" value={v?.imo || "—"} />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Stat icon={Gauge} label="Speed" value={v?.sog != null ? Number(v.sog).toFixed(1) : null} unit="kn" />
          <Stat icon={Compass} label="Course" value={v?.cog != null ? Math.round(v.cog) : null} unit="°" />
          <Stat icon={Navigation2} label="Heading" value={v?.heading != null ? Math.round(v.heading) : null} unit="°" />
          <Stat icon={Ruler} label="Size" value={v?.length ? `${v.length}×${v.beam || "?"}` : null} unit="m" />
        </div>

        <div className="rounded-lg bg-slate-800/40 border border-slate-700/50 px-3 py-1">
          <Row label="Nav status" value={navStatusText(v?.nav_status)} />
          <Row label="Destination" value={v?.destination || "—"} />
          <Row label="ETA" value={v?.eta || "—"} />
          <Row label="Last seen" value={timeAgo(v?.ts)} />
        </div>

        <div className="rounded-lg bg-slate-800/40 border border-slate-700/50 p-3 space-y-3">
          <label className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm text-slate-200">
              <MapPin className="h-4 w-4 text-cyan-400" /> Show track
            </span>
            <Switch
              data-testid="show-track-switch"
              checked={showTrack}
              onCheckedChange={setShowTrack}
            />
          </label>
          {showTrack && (
            <div className="flex gap-1.5" data-testid="track-window-selector">
              {WINDOWS.map((w) => (
                <button
                  key={w.key}
                  data-testid={`track-window-${w.key}`}
                  onClick={() => setTrackWindow(w)}
                  className="flex-1 rounded-md border px-2 py-1.5 text-xs font-mono transition-all"
                  style={{
                    borderColor: trackWindow.key === w.key ? "#06B6D4" : "rgba(51,65,85,0.6)",
                    background: trackWindow.key === w.key ? "rgba(6,182,212,0.15)" : "transparent",
                    color: trackWindow.key === w.key ? "#22d3ee" : "#94a3b8",
                  }}
                >
                  {w.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-lg bg-slate-800/30 border border-dashed border-slate-700/50 p-3 flex items-start gap-2">
          <Anchor className="h-4 w-4 text-slate-600 mt-0.5 shrink-0" />
          <p className="text-[11px] leading-relaxed text-slate-500">
            This vessel's stored track feeds SkyTruth Cerulean oil-slick correlation —
            select any slick to see nearby vessels within the detection window.
          </p>
        </div>
      </div>
    </aside>
  );
};

export default VesselPanel;
